import { Divider, Stack } from 'component-library';
import { FC, HTMLAttributes, SVGProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { BenchmarkType, DeforestationCategory, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { Dot } from '@/components/Charts/components/Dot';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { colorPalette } from '@/theme/colorPalette';
import { formatUnit } from '@/utils/formatting';

import { DeforestationBenchmarkPill } from '../Pill/BenchmarkPill';
import { Bar, Chart, ComposedChart, TooltipProps } from './components/Chart';
import { LegendButton, LegendItemProps } from './components/LegendBar';
import { RenderReferenceLine } from './ReferenceLines';
import { ChartProps, GraphFact } from './types';

const UNIT = formatUnit(UnitEnum.ha);

const CHART_COLORS = {
  [DeforestationCategory.no_loss_detected]: '#5dbd26',
  [DeforestationCategory.forest_loss]: '#c0880e',
} as const;

const SHARED_BAR_PROPS = {
  stackId: 'deforestation',
  barSize: 21,
  stroke: '#fff',
  strokeWidth: 1,
};

const BAR_CATEGORIES = Object.values(DeforestationCategory).map((category) => ({
  dataKey: category,
  fill: CHART_COLORS[`${category}`],
  className: `deforestation-${category}`,
}));

const factNames = {
  deforestation_total: 'deforestation_total',
  deforestation_total_benchmark: 'deforestation_total_benchmark',
};

export const DeforestationHistoricalChart: FC<
  HTMLAttributes<HTMLDivElement> &
    ChartProps & {
      customBenchmark?: CustomBenchmarkSuccessResponse | null;
    }
> = ({ data, customBenchmark, ...delegated }) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<DeforestationCategory[]>([]);
  const hasSelectedCategories = selectedCategories.length > 0;

  const chartConfig = useGetChartElementsConfig();
  const chartDataRaw = useChartData(data);

  const chartData = customBenchmark
    ? (chartDataRaw.map((point) => ({
        ...point,
        custom_low: customBenchmark.low_value !== null ? { value: customBenchmark.low_value } : undefined,
        custom_moderate:
          customBenchmark.moderate_value !== null ? { value: customBenchmark.moderate_value } : undefined,
        custom_high: customBenchmark.high_value !== null ? { value: customBenchmark.high_value } : undefined,
      })) as ChartDataEntry[])
    : chartDataRaw;

  const filteredBars = hasSelectedCategories
    ? BAR_CATEGORIES.filter((item) => selectedCategories.includes(item.dataKey))
    : BAR_CATEGORIES;

  const filteredChartData = !hasSelectedCategories
    ? chartData
    : chartData.reduce<ChartDataEntry[]>((acc, entry) => {
        const hasSelectedCategory = Object.keys(entry).some((key) => {
          return (
            selectedCategories.includes(key as DeforestationCategory) &&
            typeof entry[key as DeforestationCategory] === 'number'
          );
        });

        if (!hasSelectedCategory) return acc;

        return [...acc, entry];
      }, []);

  const xAxisProps = {
    dataKey: 'yearLabel',
    type: 'category' as const,
  };

  const benchmarkLabel = (
    filteredChartData.at(-1)?.[factNames.deforestation_total_benchmark] as GraphFact | undefined
  )?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.benchmark)?.value as BenchmarkType | undefined;

  const maxYAxisValue =
    filteredChartData.reduce(
      (max, entry) => {
        const selectedCategoriesNumbers = (
          selectedCategories.length > 0
            ? selectedCategories.map((category) => entry[`${category}`])
            : [entry[DeforestationCategory.forest_loss], entry[DeforestationCategory.no_loss_detected]]
        ).filter((n) => typeof n === 'number') as number[];

        return Math.max(...selectedCategoriesNumbers, max);
      },
      0.1, // 0.1 is the value give by science as the fallback max value to use when there is no data, so that the 0 values are pushed to the Y axis origin
    ) * 1.2;

  const tickFormatter = (value: number) => {
    const options =
      maxYAxisValue < 1
        ? {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2, // Changed from 1 to 2
          }
        : undefined;

    return getDisplayNumber(value, undefined, options);
  };

  return (
    <>
      <Chart.Container {...delegated} config={chartConfig} data={filteredChartData} data-testid='deforestation-chart'>
        <ComposedChart data={filteredChartData}>
          {filteredBars.map((item) => (
            <Bar
              key={item.dataKey}
              {...item}
              activeBar={(props: unknown) => (
                <CustomActiveBar {...(props as ActiveBarProps)} selectedCategories={selectedCategories} />
              )}
              {...SHARED_BAR_PROPS}
            />
          ))}
          {customBenchmark ? (
            <>
              {RenderReferenceLine({
                dataKey: 'custom_low.value',
                type: 'monotone',
                label: filteredChartData.some((point) => !!point.custom_low)
                  ? `${customBenchmark.name} (${t('global.benchmark.low')})`
                  : null,
                chartData: filteredChartData,
                id: 'custom-low',
                labelSide: 'right',
              })}
              {RenderReferenceLine({
                dataKey: 'custom_moderate.value',
                type: 'monotone',
                label: filteredChartData.some((point) => !!point.custom_moderate)
                  ? `${customBenchmark.name} (${t('global.benchmark.moderate')})`
                  : null,
                chartData: filteredChartData,
                id: 'custom-moderate',
                labelSide: 'right',
              })}
              {RenderReferenceLine({
                dataKey: 'custom_high.value',
                type: 'monotone',
                label: filteredChartData.some((point) => !!point.custom_high)
                  ? `${customBenchmark.name} (${t('global.benchmark.high')})`
                  : null,
                chartData: filteredChartData,
                id: 'custom-high',
                labelSide: 'right',
              })}
            </>
          ) : (
            RenderReferenceLine({
              dataKey: `${[factNames.deforestation_total_benchmark]}.value`,
              type: 'monotone',
              label: benchmarkLabel ? t(`global.benchmark.${benchmarkLabel}`) : null,
              chartData,
              id: factNames.deforestation_total_benchmark,
              labelSide: 'right',
            })
          )}
          <Chart.XAxis {...xAxisProps} className='deforestation-x-axis' />
          <Chart.YAxis
            className='deforestation-y-axis'
            domain={[0, maxYAxisValue]}
            allowDataOverflow={true}
            tickFormatter={tickFormatter}
          >
            <Chart.Label>{UNIT}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={<CustomTooltip />} cursor={false} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar
        categories={BAR_CATEGORIES.map((item) => item.dataKey)}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
      />
    </>
  );
};

const useGetChartElementsConfig = () => {
  const { t } = useTranslation();

  return Object.fromEntries(
    Object.values(DeforestationCategory).map((category) => [
      category,
      {
        label: t(`global.deforestationCategory.${category}`),
        color: CHART_COLORS[`${category}`],
      },
    ]),
  );
};

type ChartDataEntry = {
  [K in DeforestationCategory]?: number;
} & {
  [key: string]: GraphFact | null | number | string | { value: number } | undefined;
} & {
  yearLabel: string;
};

const useChartData = (data: GraphFact[]): ChartDataEntry[] => {
  const sortedDates = [...new Set(data.map((entry) => entry.date))].sort();

  return sortedDates.map((date) => {
    const dateData = data.filter((entry) => entry.date === date);

    const categoriesData = dateData.reduce(
      (acc, cur) => {
        const isTimeSeriesFact = cur.name === factNames.deforestation_total;

        if (!isTimeSeriesFact) {
          return acc;
        }

        const category = cur.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.deforestation)
          ?.value as DeforestationCategory;

        if (!category) return acc;

        acc[`${category}`] = (acc[`${category}`] || 0) + Number(cur.value);
        return acc;
      },
      {} as { [K in DeforestationCategory]?: number },
    );

    const year = new Date(date).getFullYear().toString();

    return {
      yearLabel: year,
      ...categoriesData,
      [factNames.deforestation_total_benchmark]: dateData.find(
        (fact) => fact.name === factNames.deforestation_total_benchmark,
      ),
    };
  });
};

type ActiveBarProps = SVGProps<SVGElement> & {
  payload: ChartDataEntry;
  value: number[];
  dataKey: string;
  background: { y: number };
  selectedCategories: DeforestationCategory[];
};

const CustomActiveBar = ({
  x,
  y,
  width,
  height,
  payload,
  value,
  dataKey,
  background,
  selectedCategories,
}: ActiveBarProps) => {
  if (!x || !y || !width || !height) return <></>;

  const topBarValue =
    selectedCategories.length > 0
      ? Object.entries(payload).reduce((acc: number, [key, categoryCount]) => {
          if (typeof categoryCount !== 'number') return acc;

          if (!selectedCategories.includes(key as DeforestationCategory)) return acc;

          return acc + categoryCount;
        }, 0)
      : Object.values(payload).reduce((acc: number, curr) => (typeof curr === 'number' ? acc + curr : acc), 0);
  const isTopBar = value.at(-1) === topBarValue;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);
  const barTop = isTopBar ? background.y : y;

  const dotProps = {
    cx: barMiddleX,
    cy: y as number,
    fill: '#000000',
    strokeWidth: 0,
    opacity: 1,
  };

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={CHART_COLORS[dataKey as DeforestationCategory]}
        stroke={SHARED_BAR_PROPS.stroke}
      />
      <line x1={barMiddleX} x2={barMiddleX} y1={barTop} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
      {isTopBar && (
        <Dot.Container {...dotProps}>
          <Dot.Active {...dotProps} fill='#000000' />
        </Dot.Container>
      )}
    </g>
  );
};

type CustomLegendBarProps = {
  categories: DeforestationCategory[];
  selectedCategories: DeforestationCategory[];
  setSelectedCategories: (c: DeforestationCategory[]) => void;
};

const CustomLegendBar = ({ categories, selectedCategories, setSelectedCategories }: CustomLegendBarProps) => {
  const { t } = useTranslation();

  const handleLegendClick = (category: DeforestationCategory) => {
    let newSelectedCategories = selectedCategories.includes(category)
      ? [...selectedCategories.filter((c) => c !== category)]
      : [...selectedCategories, category];

    if (newSelectedCategories.length === categories.length) {
      newSelectedCategories = []; // Select "All" if all the categories are selected
    }

    setSelectedCategories(newSelectedCategories);
  };

  const legendBarItems: LegendItemProps[] = [
    {
      label: t('shared.ncaDetail.details.deforestation.labels.allCategories'),
      color: null,
      isActive: selectedCategories.length === 0,
      onClick: () => setSelectedCategories([]),
    },
    ...categories.map((category) => ({
      label: t(`global.deforestationCategory.${category}`),
      color: CHART_COLORS[`${category}`],
      isActive: selectedCategories.includes(category),
      onClick: () => handleLegendClick(category),
    })),
  ];

  return (
    <Stack direction='row' centerMain spacing={2} data-testid='deforestation-chart-legend'>
      {legendBarItems.map((item) => (
        <LegendButton key={item.label} {...item} />
      ))}
    </Stack>
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload) return null;

  const deforestationData = payload.filter((entry) =>
    Object.values(DeforestationCategory).includes(entry.name as DeforestationCategory),
  );

  const tooltipContent = deforestationData.map((entry) => ({
    label: t(`global.deforestationCategory.${entry.name as DeforestationCategory}`),
    value: typeof entry?.value === 'number' ? getDisplayNumber(entry.value) : t('global.analysis.noData'),
  }));

  const totalValue = getDisplayNumber(
    deforestationData.reduce((acc, curr) => {
      if (typeof curr.value !== 'number') {
        return acc;
      }
      return acc + (curr.value as number);
    }, 0),
  );

  const benchmarkInfo = payload.find((entry) => entry.name === `${factNames.deforestation_total_benchmark}.value`);
  const benchmark = benchmarkInfo?.payload?.[factNames.deforestation_total_benchmark]?.labels?.find(
    (l: { name: PlotReportFactLabelNameEnum; value: BenchmarkType }) =>
      l.name === PlotReportFactLabelNameEnum.benchmark,
  )?.value;

  return (
    <ChartTooltip className='ml-1'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{label}</ChartTooltipTextPrimary>
        {benchmark && <DeforestationBenchmarkPill className='typography-body2' benchmarkType={benchmark} />}
      </Stack>
      {tooltipContent.map((category, i) => (
        <div key={category.label}>
          <Stack direction='row' spacing={8} className='justify-between'>
            <ChartTooltipTextSecondary>{category.label}</ChartTooltipTextSecondary>
            <ChartTooltipTextSecondary>
              {category.value} {UNIT}
            </ChartTooltipTextSecondary>
          </Stack>
          {i < tooltipContent.length - 1 && <Divider className='mt-2' />}
        </div>
      ))}
      {tooltipContent.length > 1 && (
        <>
          <Divider />
          <Stack direction='row' spacing={8} className='justify-between'>
            <ChartTooltipTextPrimary>
              {t('shared.ncaDetail.details.deforestation.labels.totalArea')}
            </ChartTooltipTextPrimary>
            <ChartTooltipTextPrimary>
              {totalValue} {UNIT}
            </ChartTooltipTextPrimary>
          </Stack>
        </>
      )}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipDeforestation };
